import { useState, useRef, useCallback, useEffect } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useSelector, useDispatch } from 'react-redux';
import { telegramWebApp } from '@/main';
import {
	incrementPerTap,
	incrementTapCounter,
	resetTapCounter,
	setCounter,
	setStickerWonDialog,
	setTotalCoinsGenerated,
} from '@/core/redux/slices';
import { CounterService } from '@/core/services';
import { CoinImage } from '@/app/shared/components';
import { toast } from 'react-toastify';

interface Animation {
	id: string;
	x: number;
	y: number;
	number: number;
}

interface RootState {
	counter: {
		counter: number;
		earn_per_tap: number;
		current_energy: number;
		level: number;
		levelImage: string;
		tapCounter: number;
	};
}

interface TouchPoint {
	x: number;
	y: number;
	lastTapTime: number;
}

export const NinjaTap: React.FC = () => {
	const DEBOUNCE_TIME = 100; // milliseconds
	const TOUCH_RADIUS = 20; // pixels

	const dispatch = useDispatch();
	const {
		counter,
		earn_per_tap,
		current_energy,
		level,
		levelImage,
		// tapCounter,
	} = useSelector((state: RootState) => state.counter);

	const [touchAnimation, setTouchAnimation] = useState<Animation[]>([]);
	const accumulatedTapsRef = useRef(0);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const isInteractingRef = useRef(false);
	const touchPointsRef = useRef<TouchPoint[]>([]);

	const handleAnimationEnd = useCallback((id: string) => {
		setTouchAnimation((prev) => prev.filter((item) => item.id !== id));
	}, []);

	const updateCounterTaps = useCallback(async () => {
		if (accumulatedTapsRef.current > 0) {
			try {
				console.log(
					'Calling service with taps:',
					accumulatedTapsRef.current
				);
				const response = await CounterService.sendCounterTaps({
					taps: accumulatedTapsRef.current,
				});

				if (response.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				dispatch(setTotalCoinsGenerated(response.totalCoinsEarned));
				dispatch(setCounter(response.currentCoins));
				dispatch(resetTapCounter());
				accumulatedTapsRef.current = 0;
			} catch (error) {
				console.error('Error counter taps:', error);
				let err = 'This season is ending soon! The community is preparing a snapshot. Keep accumulating points in the new season.';
				const responseData = (error as any)?.response?.data;
				let message = '';

				// Handle case: backend sends JSON string (not object)
				if (typeof responseData === 'string') {
					try {
					const parsed = JSON.parse(responseData);
					message = parsed?.message || '';
					} catch {
					// fallback if parsing fails
					const match = responseData.match(/"message"\s*:\s*"([^"]+)"/);
					message = match ? match[1] : responseData;
					}
				}

				// ✅ check if message contains "sol"
				if (message.toLowerCase().includes('sol')) {
				
					const clickable = `
						<span style="display: block;">
							<b>🥷 Welcome to Shuriken! The Tap-To-Pump launcher on Solana</b><br/>
							1.Join the community at <a href="https://t.me/shurikendotfun" target="_blank" rel="noopener noreferrer" style="color:#00acee; font-weight:bold;">@shurikendotfun</a><br/>
							2.Create your wallet on <a href="https://t.me/dojo_pump_bot" target="_blank" rel="noopener noreferrer" style="color:#00acee; font-weight:bold;">@dojo_pump_bot</a> and add at least <b>0.01 SOL</b> to join the action!
						</span>
					`;
					const safeHtml = DOMPurify.sanitize(clickable);

					toast.info(() => parse(safeHtml));
				}else{
					toast.error(
						err
					);
				}
			}
		}
	}, [dispatch]);

	const debouncedUpdateCounterTaps = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			if (!isInteractingRef.current) {
				updateCounterTaps();
			}
		}, 1000);
	}, [updateCounterTaps]);

	const handleInteractionStart = useCallback(
		(x: number, y: number) => {
			const now = Date.now();
			const existingTouchPoint = touchPointsRef.current.find(
				(point) =>
					Math.abs(point.x - x) <= TOUCH_RADIUS &&
					Math.abs(point.y - y) <= TOUCH_RADIUS
			);

			if (existingTouchPoint) {
				if (now - existingTouchPoint.lastTapTime < DEBOUNCE_TIME) {
					return; // Ignore taps that are too close together in time and space
				}
				existingTouchPoint.lastTapTime = now;
			} else {
				touchPointsRef.current.push({ x, y, lastTapTime: now });
			}

			isInteractingRef.current = true;
			if (current_energy <= earn_per_tap) return;

			const uniqueId = `${Date.now()}-${Math.random()}`;

			telegramWebApp.HapticFeedback.impactOccurred('heavy');

			dispatch(incrementPerTap());
			dispatch(incrementTapCounter());
			accumulatedTapsRef.current += 1;

			setTouchAnimation((prev) => [
				...prev,
				{ id: uniqueId, x, y, number: earn_per_tap },
			]);

			debouncedUpdateCounterTaps();
		},
		[current_energy, earn_per_tap, dispatch, debouncedUpdateCounterTaps]
	);

	const handleInteractionEnd = useCallback(() => {
		isInteractingRef.current = false;
		touchPointsRef.current = [];
		debouncedUpdateCounterTaps();
	}, [debouncedUpdateCounterTaps]);

	useEffect(() => {
		const container = containerRef.current;
		if (container) {
			const handleTouchStart = (e: TouchEvent) => {
				e.preventDefault();
				Array.from(e.touches).forEach((touch) => {
					handleInteractionStart(touch.clientX, touch.clientY);
				});
			};

			const handleMouseDown = (e: MouseEvent) => {
				handleInteractionStart(e.clientX, e.clientY);
			};

			container.addEventListener('touchstart', handleTouchStart, {
				passive: false,
			});
			container.addEventListener('touchend', handleInteractionEnd);
			container.addEventListener('touchcancel', handleInteractionEnd);
			container.addEventListener('mousedown', handleMouseDown);
			container.addEventListener('mouseup', handleInteractionEnd);
			container.addEventListener('mouseleave', handleInteractionEnd);

			return () => {
				container.removeEventListener('touchstart', handleTouchStart);
				container.removeEventListener('touchend', handleInteractionEnd);
				container.removeEventListener(
					'touchcancel',
					handleInteractionEnd
				);
				container.removeEventListener('mousedown', handleMouseDown);
				container.removeEventListener('mouseup', handleInteractionEnd);
				container.removeEventListener(
					'mouseleave',
					handleInteractionEnd
				);
			};
		}
	}, [handleInteractionStart, handleInteractionEnd]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	return (
		<div
			ref={containerRef}
			className="flex flex-1 flex-col justify-center ninja-tap--container w-full touch-none select-none"
		>
			{level > 0 && (
				<div
					className={`rounded-full mx-auto ninja-button counter-tap bg-tap-gradient-${level}`}
				>
				<img
					className="cursor-pointer mx-auto p-6 ninja-image drop-shadow-lg"
					src={levelImage}
					alt="Ninja"
					draggable="false"
				/>
			</div>
		)}			{counter > 0 &&
				touchAnimation.map((animation) => (
					<div
						key={animation.id}
						className="animated-ninja"
						style={{
							left: animation.x - 40,
							top: animation.y - 10,
						}}
						onAnimationEnd={() => handleAnimationEnd(animation.id)}
					>
						<CoinImage width={'30px'} />
						<span className="shuriken-number text-sm font-bold">
							+{animation.number}
						</span>
					</div>
				))}
		</div>
	);
};
